import { Accordion, AccordionDetails, AccordionSummary, Backdrop, Box, Button, Card, CardContent, Checkbox, CircularProgress, Alert,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText, Grid, Link, Radio, Stack, TextField, Typography } from "@mui/material";
import ArrowRightIcon from '~/icons/ArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useRecordContext, useEditContext } from "ra-core";
import { FC, useEffect, useState } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import Edit from "~/controller/Edit";
import { DataAnalyzerSettingDto } from "~/gen/api/securefile";
import WithSave from "~/controller/WithSave";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import StringArrayTextField from "~/components/input/StringArrayTextField";
import { useResourceRegister } from "~/utils/resourceRegister";
import { crudGetOneEx } from "~/actions/crudFetchEx";
import retrieveAxiosErr from "~/utils/retrieveAxiosErr";


interface AutoDiscSettings {
  autoDiscoveryEnabled: boolean;
  scanIntervalMinutes: number;
  scanRootFolders: string[];
  dataAnalyzerEnabled: boolean;
  submit: any;
}

const autoDiscoveryResource = "settings-clients-securefile-autodiscovery"
const checkOnpremiseAnalyzerRes = 'secure-files-check-onpremise-analyzer'

const AutoDiscoverySettings: FC = () => {
  const savedHandlers = useDefaultSavedHandlers("Save SecureFile Settings")
  useResourceRegister({ name: checkOnpremiseAnalyzerRes })
  
  return (
    <Edit resource={autoDiscoveryResource} basePath="" id="edit" {...savedHandlers} mutationMode="pessimistic">
      <AutoDiscoverySettingsBody />
    </Edit>
  )
}

const initialVal = (record: DataAnalyzerSettingDto | undefined) => ({
  settings: {
    autoDiscoveryEnabled: record?.autoDiscoveryEnabled ?? true,
    scanIntervalMinutes: record?.scanIntervalMinutes ?? 5,
    scanRootFolders: record?.scanRootFolders ?? [],
    dataAnalyzerEnabled: record?.dataAnalyzerEnabled ?? false,
  },
  tmpRootFolderWarning: null as string|null,
  warningTimer: null as number|null,
  waitingCheckOnpremisesAnalyzer: false,
  checkOnpremiseAnalyzer: null as {isConfigured:boolean}|null,
  touched: null as {[k in keyof AutoDiscSettings]+?: boolean}|null,
  errors: null as {[k in keyof AutoDiscSettings]+?: string}|null,
})

const AutoDiscoverySettingsBody: FC = () => {
  const record : DataAnalyzerSettingDto|undefined = useRecordContext() as any
  const { loaded } = useEditContext()
  const [states, setStates] = useState(initialVal(record))
  useEffect(() => {
    if (record) {
      setStates(initialVal(record))
    }
  }, [record])

  const validate = (s1: typeof states) => {
    const errors: any[] = []
    // if (s1.settings.autoDiscoveryEnabled && s1.settings.scanRootFolders.length === 0) {
    //   errors.push({ scanRootFolders: 'Scan Root Folders is required' })
    // }
    return errors.length === 0 ? null : errors.reduce((acc, x) => ({...acc, ...x}), {})
  }
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const history = useHistory()
  useEffect(() => {
    return history.block((newLocation, a) => {
      let pass = !Boolean(states.touched)
      return a === 'PUSH' && !pass ? 'There are unsaved edits, discard edits and continue?' : undefined
    })
  }, [states, record, history])
  

  return (
    <>
      {states.errors?.submit && (
        <Box sx={{ mt: 3 }}>
          <FormHelperText error>
            {states.errors?.submit}
          </FormHelperText>
        </Box>
      )}
      <Stack direction="row" sx={{ pb: 2 }} >
        <Button sx={{ ml: 'auto', mr: 2 }}
          color="primary"
          variant="outlined"
          onClick={() => { setStates(initialVal(record)) }}
        >
          Discard
        </Button>
        <WithSave 
          render={({ save, saving}) =>
            <Button
              color="primary"
              disabled={!states.touched || saving}
              type="submit"
              variant="contained"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                setStates(v => {
                  const v1 = {...v,
                    touched: {
                      autoDiscoveryEnabled: true,
                      scanIntervalMinutes: true,
                      scanRootFolders: true,
                    },
                  }
                  v1.errors = validate(v1)
                  if (!v1.errors) {  
                    setTimeout(() => {
                      save({...v1.settings}, {
                        onSuccess: () => {
                          setStates(v2 => ({...v2, touched: null}))
                        },
                        onFailure: (err:any) => {
                          setStates(v2 => ({...v2, errors: { submit: err.message }}))
                        },
                      })
                    })
                  }
                  return v1
                })
                return false
              }}
            >
              Save
            </Button>
          }
        />
      </Stack>

      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="space-between">
                <FormControl>
                  <FormControlLabel
                    label="Enable Auto Discovery"
                    control={
                      <Checkbox checked={states.settings.autoDiscoveryEnabled} 
                        onChange={(e) => setStates(v => ({...v,
                          touched: {...v.touched, autoDiscoveryEnabled: true},
                          settings: {...v.settings, autoDiscoveryEnabled: e.target.checked}
                        }))}
                      />
                    }
                  />
                </FormControl>
                <Button variant="text" color="primary" sx={{ mr: 2 }}
                  endIcon={<ArrowRightIcon fontSize="small" />}
                  component={RouterLink}
                  to="/settings/securefile/per-client-setting"
                >
                  Configure Per Client
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="space-between">
                <FormControl>
                  <FormControlLabel
                    label="Enable Data Analyzer"
                    control={
                      <Checkbox checked={states.settings.dataAnalyzerEnabled} 
                        onChange={(e) => {
                          if (e.target.checked) { 
                            dispatch(crudGetOneEx(checkOnpremiseAnalyzerRes, 'edit', '', {
                              onSuccess: (resp) => {
                                const isConfigured = Boolean(resp?.data?.isConfigured)
                                setTimeout(() => setStates(v1 => ({...v1, waitingCheckOnpremisesAnalyzer: false,
                                  checkOnpremiseAnalyzer: {isConfigured},
                                  ...(isConfigured && { 
                                    settings: {...v1.settings, dataAnalyzerEnabled:true},
                                    touched: {...v1.touched, dataAnalyzerEnabled:true}
                                  }),
                                })))
                              },
                              onFailure: (err) => {
                                const errMsg = retrieveAxiosErr(err)
                                enqueueSnackbar(`Check status failed: ${errMsg}`, { variant: 'error' })
                                setTimeout(() => setStates(v1 => ({...v1, waitingCheckOnpremisesAnalyzer: false})))
                              },
                            }))
                          }
                          setStates(v => 
                            e.target.checked
                              ? {...v, waitingCheckOnpremisesAnalyzer: true} 
                              : ({...v,
                                touched: {...v.touched, dataAnalyzerEnabled: true},
                                settings: {...v.settings, dataAnalyzerEnabled: false}
                              })
                          )
                        }}
                      />
                    }
                  />
                </FormControl>
                <Button variant="text" color="primary" sx={{ mr: 2 }}
                  endIcon={<ArrowRightIcon fontSize="small" />}
                  component={RouterLink}
                  to="/settings/securefile/internal-data-analyzer"
                >
                  Configure Data Analyzer
                </Button>
              </Stack>
              {states.checkOnpremiseAnalyzer &&
                <EnableAnalyzerConfirm checkOnpremiseAnalyzer={states.checkOnpremiseAnalyzer} 
                  onConfirm={()=>{
                    setStates(v => ({...v,
                      settings: {...v.settings, dataAnalyzerEnabled: true },
                      checkOnpremiseAnalyzer: null,
                      touched: {...v.touched, dataAnalyzerEnabled: true},
                    }))
                  }}
                  onClose={()=>{
                    setStates(v => ({...v, checkOnpremiseAnalyzer: null}))
                  }}
                />}
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{mt:1}}>Scan Interval: {"  "}
                  <Typography variant="body2" component="span" display="inline">every{" "}</Typography>
                </Typography>
                <TextField size="small" type="number" value={states.settings.scanIntervalMinutes} 
                  onChange={(e) => {
                    let n = Number(e.target.value)
                    if (isNaN(n)) { return }
                    else if (n < 3) { n = 3 }
                    n = Math.round(n)
                    setStates(v => ({...v,
                      settings: {...v.settings, scanIntervalMinutes: n},
                      touched: {...v.touched, scanIntervalMinutes: true},
                    }))
                  }}
                />
                <Typography variant="body2" sx={{mt:theme=>`${theme.spacing(1.25)}!important`}}>minutes.</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" >
                Please enter a list of root folders that you would like to scan for sensitive data. All subfolders within the root folders will be scanned recursively. <br /> 
                You may use environmental variables in the root folders, such as <b>%UserProfile%</b>\Documents.
              </Alert>
              <StringArrayTextField list={states.settings.scanRootFolders} stackChips="column"
                sx={{ mt: 3 }}
                label="Scan Root Folders"
                placeholder="Add a new Root Folder and press the Enter key to confirm..."
                error={Boolean(states.touched?.scanRootFolders && states.errors?.scanRootFolders)}
                helperText={states.touched?.scanRootFolders && states.errors?.scanRootFolders}
                setList={(ls) => {
                  const filtered = filterFolderListWithoutSub(ls)
                  setStates(v => {
                    const touched = filtered !== ls || ls !== v.settings.scanRootFolders
                    let patch = null as any
                    if (filtered !== ls) { // there were subfolders been merged and reduced
                      if (v.warningTimer) { window.clearTimeout(v.warningTimer) }
                      patch = {
                        tmpRootFolderWarning: 'Subfolders have been merged.',
                        warningTimer: window.setTimeout(() => {
                          setStates(v1 => ({...v1, tmpRootFolderWarning: null, warningTimer: null}))
                        }, 5000)
                      }
                    }
                    return !touched ? v : {...v,
                      settings: {...v.settings, scanRootFolders: filtered},
                      touched: {...v.touched, scanRootFolders: true},
                      ...patch
                    }
                  })
                }}
                acceptValue={(s) => {
                  const errorStr = validateRootFolderString(states.settings.scanRootFolders, s)
                  if (errorStr != null) {
                    setTimeout(() => {  
                      setStates(v => {
                        if (v.warningTimer) { window.clearTimeout(v.warningTimer) }
                        return {...v,
                          tmpRootFolderWarning: errorStr,
                          warningTimer: window.setTimeout(() => {
                            setStates(v1 => ({...v1, tmpRootFolderWarning: null, warningTimer: null}))
                          }, 5000)
                        }
                      })
                    })
                  }
                  return !errorStr
                }}
              />
              {states.tmpRootFolderWarning &&
                <FormHelperText sx={{ color: 'warning.main', ml: 2 }}>
                  {states.tmpRootFolderWarning}
                </FormHelperText>}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Backdrop open={states.waitingCheckOnpremisesAnalyzer} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress />
      </Backdrop>
    </>
  )
}

export default AutoDiscoverySettings;

const {validateRootFolderString, filterFolderListWithoutSub} = (() => { 
  const formatPattern = /^[a-zA-Z]{1}\:\\.+$/i
  const formatPattern2 = /^\%[a-zA-Z0-9_\s]+\%[\\\w\s\.]*.*$/i
  const banList = [
    "C:",
    "C:\\",
    "C:\\Users"
  ]
  const patternList = [
    /^[a-zA-Z]{1}\:\\program files(\s\(x86\))?[\\\w\s\.]*$/i,
    /^[a-zA-Z]{1}\:\\\$[\w\.]+[\\\w\s\.]*$/i,
    /^\%userprofile\%[\\]*$/i,
    /^c\:\\users\\[\w\s\.]+\\*$/i,
    /^c\:\\programdata[\\\w\s\.]*$/i,
    /^c\:\\recovery[\\\w\s\.]*$/i,
    /^c\:\\windows[\\\w\s\.]*$/i,
    /^c\:\\onedrivetemp[\\\w\s\.]*$/i,
    /^c\:\\users\\[\w\s\.]+\\appdata[\\\w\s\.]*$/i,
    /^c\:\\users\\[\w\s\.]+\\ondata[\\\w\s\.]*$/i
  ]
  const normalize = (s:string) => {
    return s.trim().replace(new RegExp('\\\\$','g'), '').toLowerCase()
  }
  const ckeckDuplication = (currentStr: string, challengeStr: string): string|undefined => {
    if (currentStr === challengeStr) {
      return 'Same value already exists.'
    }
    //do not allow to add C:\My Folder\Sub Folder if C:\My Folder already exists
    if (challengeStr.length > currentStr.length 
     && challengeStr.indexOf(currentStr + '\\') === 0
    ) {
      return 'A parent folder of the input value already exists.'
    }
  }

  return {
    validateRootFolderString: (currentList: string[], challengeStr: string) => {
      if(!formatPattern.test(challengeStr.trim()) && !formatPattern2.test(challengeStr.trim())){
        return 'This does not appear to be a valid folder.'
      }
      const nChallengeStr = normalize(challengeStr)
      for(const current of currentList){
        const nCurrent = normalize(current)
        const errorStr = ckeckDuplication(nCurrent, nChallengeStr)
        if (errorStr != null) { return errorStr }
      }
      for(const ban of banList){
        if(ban.toLowerCase() === nChallengeStr){
          return 'This value is not allowed.'
        }
      }
      for(const pattern of patternList){
        if(pattern.test(nChallengeStr)){
          return 'This value is not allowed.'
        }
      }
    },
    filterFolderListWithoutSub: (currentList: string[]) => {
      const pairs = currentList.map((s, i) => [normalize(s), i] as const).sort((a, b) => a[0].length <= b[0].length ? -1 : 1)
      const subfolders = new Set<number>()
      for(let j = 0; j < pairs.length - 1; j++) {
        const [s, i] = pairs[j]
        if (subfolders.has(i)) { continue; }
        const s_ = s + '\\'
        for(let n = j + 1; n < pairs.length; n++) {
          const other = pairs[n]
          if (subfolders.has(other[1])) { continue; }
          if (other[0].indexOf(s_) === 0) {
            subfolders.add(other[1])
          }
        }
      }
      return subfolders.size === 0 ? currentList :
        currentList.filter((s, i) => !subfolders.has(i))
    }
  }
})();


const EnableAnalyzerConfirm : FC<{ checkOnpremiseAnalyzer: {isConfigured:boolean}|null, onClose: ()=>void, onConfirm: ()=>void, }> = ({
  checkOnpremiseAnalyzer, onClose, onConfirm: handleConfirm
}) => {
  const [states, setStates] = useState({
    useOnpremises: true,
  })
  const handleChange = (isOnpremises: boolean) => (x:any, expanded: boolean) => {
    setStates(v => ({...v, useOnpremises: isOnpremises ? expanded : !expanded}))
  }
  return (
    <Dialog
      open={!!checkOnpremiseAnalyzer}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
    >
      <DialogTitle id="alert-dialog-title">
        Enable Data Analyzer
      </DialogTitle>
      <DialogContent
        sx={{ mt: 1,
          '& div.MuiAccordion-root.MuiPaper-root': {
            mt: '1px', mb: '-1px',
            borderRadius: 'unset',
            '& .MuiAccordionSummary-root': {
              bgcolor: theme => theme.palette.background.default,
            },
          },
        }}
      >
        {checkOnpremiseAnalyzer?.isConfigured
        ? <>
            <Typography sx={{mb: 2}}>
              The Data Analyzer service deployed in your own network environment is used. Your sensitive data will not leave your network environment.
            </Typography>
            <Stack direction="row-reverse" >
              <Button variant="outlined" color="info" onClick={onClose}>Ok</Button>
            </Stack>
          </>
        : <>
            <Accordion expanded={states.useOnpremises} onChange={handleChange(true)}>
              <AccordionSummary aria-controls="panel1d-content" id="panel1d-header" expandIcon={<ExpandMoreIcon />}>
                <FormControlLabel value="female" control={<Radio checked={states.useOnpremises} />} label="Use Internal Data Analyzer" />
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">
                  Deploy and configure the Data Analyzer service in your own network environment. Your sensitive data will not leave your network environment. <br/>
                  Please <Link sx={{lineHeight:'36px'}} component={RouterLink} to="/settings/securefile/internal-data-analyzer" >Configure Data Analyzer</Link> {" "}
                  before proceeding with this option.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion expanded={!states.useOnpremises} onChange={handleChange(false)}>
              <AccordionSummary aria-controls="panel2d-content" id="panel2d-header" expandIcon={<ExpandMoreIcon />}>
                <FormControlLabel value="female" control={<Radio checked={!states.useOnpremises} />} label="Use Managed Data Analyzer" />
              </AccordionSummary>
              <AccordionDetails>
                <Stack alignItems="center">
                  <Typography component="span" variant="body2" sx={{ ml: 1}}>
                    The Managed Data Analyzer is hosted by the OnData platform. If you choose to use the Managed Data Analyzer, 
                    your sensitive data will be securely sent to the OnData platform for analysis. The OnData platform will discard 
                    the data received after the analysis and won't store a copy of the data. 
                    Please click on the Confirm button if you are certain to proceed with this option.
                  </Typography>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </>}
      </DialogContent>
      {!states.useOnpremises &&
        <DialogActions>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirm} variant="contained">
            Confirm
          </Button>
        </DialogActions>}
    </Dialog>
  )
}
